import { memo, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';

import { useTrackDuplicatedAsset } from '@air/analytics';
import { Clips } from '@air/api';
import { ClipType } from '@air/api/types';

import { DuplicateAssetTaskCompletedPanelItem } from '~/components/DuplicateAssetTasks/DuplicateAssetTaskCompletedPanelItem';
import { DuplicateAssetTaskFailedPanelItem } from '~/components/DuplicateAssetTasks/DuplicateAssetTaskFailedPanelItem';
import { DuplicateAssetTaskInProgressPanelItem } from '~/components/DuplicateAssetTasks/DuplicateAssetTaskInProgressPanelItem';
import { PaneContainer } from '~/components/FileStatusTrackingPane/PaneContainer';
import { GalleryItemType } from '~/components/Gallery/types';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSocketConnectionChange } from '~/providers/SocketContext/hooks/useSocketConnectionChange';
import { removeTasksAction } from '~/store/tasks/actions';
import { makeDuplicateAssetTasksSelectorByWorkspaceId } from '~/store/tasks/selectors';
import { ReadyState } from '~/types/sockets';
import { useAddAssetsToAllViews } from '~/utils/mutateUtils/GalleryAssets';
import { useAirSelector } from '~/utils/ReduxUtils';
import { useCancelTask } from '~/utils/taskUtils/useCancelTask';
import { useHideTask } from '~/utils/taskUtils/useHideTask';
import { usePrivateSyncTasks } from '~/utils/taskUtils/usePrivateSyncTasks';

export const PrivateDuplicateAssetTasksPanel = memo(() => {
  const { currentWorkspace } = useCurrentWorkspace();
  const tasksSelector = useMemo(
    () => makeDuplicateAssetTasksSelectorByWorkspaceId(currentWorkspace?.id),
    [currentWorkspace?.id],
  );
  const dispatch = useDispatch();
  const duplicateAssetTasks = useAirSelector(tasksSelector);
  const { cancelTask } = useCancelTask();
  const { hideTask } = useHideTask();
  const { addAssetsToAllViews } = useAddAssetsToAllViews();
  const { trackDuplicatedAsset } = useTrackDuplicatedAsset();

  const { loadFromStorage, syncLocalTasks } = usePrivateSyncTasks({
    workspaceId: currentWorkspace?.id,
    tasksSelector,
    localType: 'DuplicateAsset',
    remoteType: 'ContentDuplicator',
    onComplete: async ({ task, localTask }) => {
      // The task can complete after the user switches workspaces, so read the workspace it was created in rather than the live context value.
      const workspaceId = localTask.metadata.workspaceId;

      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      const duplicatedClip = task.data?.duplicatedRootClips?.[0];

      if (!duplicatedClip) {
        return;
      }

      const clip = await Clips.get({ workspaceId, clipId: duplicatedClip.id });

      if (!clip) {
        return;
      }

      const targetBoardId = localTask.metadata.targetBoardId;

      await addAssetsToAllViews({
        parentBoardId: targetBoardId,
        assets: [clip],
        type: clip.type === ClipType.nonMedia ? GalleryItemType.file : GalleryItemType.asset,
      });

      if (localTask.metadata.trackLocation) {
        trackDuplicatedAsset({
          assetId: duplicatedClip.assetId,
          sourceAssetId: localTask.metadata.sourceAssetId,
          location: localTask.metadata.trackLocation,
          boardId: targetBoardId,
          isFile: localTask.metadata.isFile,
        });
      }
    },
  });

  useEffect(() => {
    if (currentWorkspace?.id) {
      loadFromStorage();
    }
  }, [loadFromStorage, currentWorkspace?.id]);

  useSocketConnectionChange({
    onChange: (readyState) => {
      if (readyState === ReadyState.OPEN) {
        syncLocalTasks();
      }
    },
  });

  useUnmount(() => {
    dispatch(
      removeTasksAction({
        taskIds: duplicateAssetTasks.map(({ localTaskId }) => localTaskId),
      }),
    );
  });

  if (duplicateAssetTasks.length < 1) {
    return null;
  }

  return (
    <div>
      {duplicateAssetTasks.map((duplicateAssetTask) => {
        return (
          <PaneContainer key={duplicateAssetTask.localTaskId} className="mb-2 last:mb-0">
            {!duplicateAssetTask.hidden && duplicateAssetTask.status === 'in-progress' && (
              <DuplicateAssetTaskInProgressPanelItem onCancel={hideTask} {...duplicateAssetTask} />
            )}
            {duplicateAssetTask.status === 'completed' && (
              <DuplicateAssetTaskCompletedPanelItem onClear={cancelTask} {...duplicateAssetTask} />
            )}
            {duplicateAssetTask.status === 'error' && (
              <DuplicateAssetTaskFailedPanelItem onClear={cancelTask} {...duplicateAssetTask} />
            )}
          </PaneContainer>
        );
      })}
    </div>
  );
});

PrivateDuplicateAssetTasksPanel.displayName = 'PrivateDuplicateAssetTasksPanel';
